import { useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  Badge,
  Category,
  Delete,
  Description,
  Email,
  Image,
  Language,
  Link as LinkIcon,
  LocationOn,
  Payment,
  Phone,
  QrCode2,
  ChevronRight,
  OpenInNew,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell, { SettingsGroup, SettingsGroupLabel } from '../components/layout/AppShell';
import { IconTile } from '../components/layout/IconTile';
import DesignSystem from '../designSystem';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import {
  ApiBusiness,
  fetchCategories,
  deleteVisiBusiness,
  fetchDeleteVisiReasons,
  fetchSocialTypes,
  fetchUserBusinesses,
  getBusinessLogoUrl,
  getBusinessCoverUrl,
  appendIosPhoneFields,
  postUpdateBusinessIos,
  ReportReason,
  SocialTypeItem,
} from '../services/api';
import { useT } from '../i18n/useT';
import {
  isStrictSpotifyArtistsType,
  normalizeSpotifyArtistInput,
  shouldNormalizeSpotifyArtistType,
} from '../utils/spotifyArtistUrl';

const tiles = DesignSystem.colors.iconTile;
type SectionKey =
  | 'basics'
  | 'category'
  | 'biography'
  | 'website'
  | 'phones'
  | 'emails'
  | 'address'
  | 'socials'
  | 'media'
  | 'payments'
  | 'delete';

export default function VisiEditHubPage() {
  useThemeMode();
  const { businessId } = useParams<{ businessId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [business, setBusiness] = useState<ApiBusiness | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [section, setSection] = useState<SectionKey | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [biography, setBiography] = useState('');
  const [website, setWebsite] = useState('');
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [email1, setEmail1] = useState('');
  const [email2, setEmail2] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState('0');
  const [lng, setLng] = useState('0');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [subsubcategoryId, setSubsubcategoryId] = useState('');
  const [tree, setTree] = useState<
    Array<{
      id: number;
      name: string;
      subcategories?: Array<{
        id: number;
        name: string;
        subsubcategories?: Array<{ id: number; name: string }>;
      }>;
    }>
  >([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverFiles, setCoverFiles] = useState<(File | null)[]>([null, null, null, null]);
  const [socialTypes, setSocialTypes] = useState<SocialTypeItem[]>([]);
  const [socialTypeName, setSocialTypeName] = useState('');
  const [socialValue, setSocialValue] = useState('');
  const [twint, setTwint] = useState('');
  const [paypal, setPaypal] = useState('');
  const [deleteReasons, setDeleteReasons] = useState<ReportReason[]>([]);
  const [deleteReasonId, setDeleteReasonId] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');

  const reload = async () => {
    if (!user?.token || !businessId) return;
    const id = parseInt(businessId, 10);
    const res = await fetchUserBusinesses(user.token);
    const b = (res.businesses || []).find((x) => x.id === id);
    if (!b) {
      setLoadErr(t('visiEditNotFound'));
      return;
    }
    setBusiness(b);
    setName((b.name || '').replace(/\+/g, ' '));
    setUsername((b.username || '').replace(/^@/, ''));
    setBiography(b.description || '');
    setWebsite(b.web || '');
    setPhone1((b.phones?.[0]?.phone || '').replace(/^\+/, ''));
    setPhone2((b.phones?.[1]?.phone || '').replace(/^\+/, ''));
    setEmail1(b.emails?.[0]?.email || '');
    setEmail2(b.emails?.[1]?.email || '');
    const a = b.addresses?.[0];
    setAddress(a?.address || a?.city || '');
    setLat(a?.latitude != null ? String(a.latitude) : '0');
    setLng(a?.longitude != null ? String(a.longitude) : '0');
    if (b.category?.id) setCategoryId(String(b.category.id));
    if (b.subcategory?.id) setSubcategoryId(String(b.subcategory.id));
    if (b.subsubcategory?.id) setSubsubcategoryId(String(b.subsubcategory.id));
    const tw = b.socials?.find((s) => (s.type?.name || '').toLowerCase().includes('twint'));
    const pp = b.socials?.find((s) => (s.type?.name || '').toLowerCase().includes('paypal'));
    const socialHandle = (s?: NonNullable<ApiBusiness['socials']>[number]) =>
      (s?.social || s?.url || (s as { name?: string } | undefined)?.name || '').trim();
    setTwint(socialHandle(tw));
    setPaypal(socialHandle(pp));
  };

  useEffect(() => {
    if (!user?.token) return;
    reload().catch((e) => setLoadErr(e instanceof Error ? e.message : t('visiEditLoadFailed')));
    fetchCategories(undefined, user.token)
      .then((res) => setTree((res.categories || []) as typeof tree))
      .catch(() => undefined);
    fetchSocialTypes(user.token)
      .then(setSocialTypes)
      .catch(() => undefined);
    fetchDeleteVisiReasons(user.token, getApiLanguage())
      .then((r) => {
        setDeleteReasons(r);
        if (r[0]) setDeleteReasonId(String(r[0].id));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, businessId]);

  const subs = useMemo(() => {
    const c = tree.find((x) => String(x.id) === categoryId);
    return c?.subcategories || [];
  }, [tree, categoryId]);

  const subsubs = useMemo(() => {
    const s = subs.find((x) => String(x.id) === subcategoryId);
    return s?.subsubcategories || [];
  }, [subs, subcategoryId]);

  const socialHandleOf = (s: NonNullable<ApiBusiness['socials']>[number]): string =>
    (s.social || s.url || (s as { name?: string }).name || '').trim();

  const existingSocialValueOf = (typeName: string): string => {
    const match = (business?.socials || []).find((s) => s.type?.name === typeName);
    return match ? socialHandleOf(match) : '';
  };

  const appendSocialsToFd = (fd: FormData, extras: Array<{ typeName: string; value: string }> = []) => {
    // updateBusinessIos deletes all socials then recreates from Type,pos keys — always re-send.
    const byType = new Map<string, string>();
    for (const s of business?.socials || []) {
      const typeName = s.type?.name;
      const val = socialHandleOf(s);
      if (!typeName || !val) continue;
      byType.set(typeName, val);
    }
    for (const ex of extras) {
      if (ex.value.trim()) byType.set(ex.typeName, ex.value.trim());
      else byType.delete(ex.typeName);
    }
    let i = 1;
    byType.forEach((val, typeName) => {
      fd.append(`${typeName},${i}`, val);
      i += 1;
    });
  };

  const buildBaseFd = (): FormData => {
    const fd = new FormData();
    fd.append('business_id', String(businessId));
    fd.append('name', name.trim() || business?.name || 'VISI');
    fd.append('username', username.trim().replace(/^@/, '') || (business?.username || '').replace(/^@/, ''));
    fd.append('chosenType', '1');
    fd.append('user_id', String(user?.id ?? ''));
    // PlaceType::fixed=1 (needs address), gps=2; 3 is search-only ("both")
    const existingPlace = business?.place_type_id;
    const placeType =
      section === 'address' && address.trim() && address.trim().toLowerCase() !== 'dynamic'
        ? 1
        : existingPlace === 1 || existingPlace === 2
          ? existingPlace
          : 2;
    fd.append('place_type', String(placeType));
    fd.append('lat', lat || '0');
    fd.append('lng', lng || '0');
    fd.append('address', address);
    fd.append('biography', biography || ' ');
    fd.append('web', website);
    if (subcategoryId) fd.append('chosenSubcategory', subcategoryId);
    if (subsubcategoryId) fd.append('chosenSubsubcategory', subsubcategoryId);
    if (email1) fd.append('email1', email1);
    if (email2) fd.append('email2', email2);
    // Phones: match iOS (phone1/number1/country1/extras1)
    if (phone1) appendIosPhoneFields(fd, phone1, 1);
    if (phone2) appendIosPhoneFields(fd, phone2, 2);
    return fd;
  };

  const saveSection = async () => {
    if (!user?.token || !businessId) return;
    setSaving(true);
    setSaveErr(null);
    setSaveMsg(null);
    try {
      const fd = buildBaseFd();
      if (section === 'media') {
        if (!logoFile && !coverFiles.some(Boolean)) {
          setSaveErr(t('visiEditChooseMediaFirst'));
          setSaving(false);
          return;
        }
        if (logoFile) fd.append('image1', logoFile);
        coverFiles.forEach((file, idx) => {
          if (file) fd.append(`cover${idx + 1}`, file);
        });
      }
      const socialExtras: Array<{ typeName: string; value: string }> = [];
      if (section === 'socials' && socialTypeName && socialValue.trim()) {
        // allSocials hides the id, so name is the only stable key — and it is what the server matches on
        const st = socialTypes.find((s) => s.name === socialTypeName);
        // iOS uses raw SocialType.name (spaces kept); server also accepts underscores→spaces
        const typeName = st?.name ?? socialTypeName;
        let value = socialValue.trim();
        if (shouldNormalizeSpotifyArtistType(typeName)) {
          const normalized = normalizeSpotifyArtistInput(value);
          if (normalized.ok) {
            value = normalized.artistId;
          } else if (isStrictSpotifyArtistsType(typeName) && normalized.reason !== 'not-url') {
            // Strict Artists type rejects non-artist Spotify URLs; plain Spotify only rewrites artist paths.
            throw new Error(t('visiEditSpotifyArtistUrlOnly'));
          }
        }
        socialExtras.push({ typeName, value });
      }
      if (section === 'payments') {
        socialExtras.push({ typeName: 'Twint', value: twint.trim() });
        socialExtras.push({ typeName: 'PayPal', value: paypal.trim() });
      }
      if (section !== 'delete') {
        appendSocialsToFd(fd, socialExtras);
      }
      if (section === 'delete') {
        await deleteVisiBusiness(
          parseInt(businessId, 10),
          deleteReasonId,
          deleteMessage,
          user.token,
          getApiLanguage()
        );
        navigate('/profile');
        return;
      }
      await postUpdateBusinessIos(fd, user.token);
      setSaveMsg(t('visiEditSaved'));
      setLogoFile(null);
      setCoverFiles([null, null, null, null]);
      await reload();
      setSection(null);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : t('visiEditSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const uname = username || (business?.username || '').replace(/^@/, '');
  const logo = business?.logo ? getBusinessLogoUrl(business.logo) : null;

  const rows: Array<{ key: SectionKey; label: string; icon: ReactElement; subtitle?: string; tile?: string }> = useMemo(
    () => [
      { key: 'basics', label: t('visiEditSectionBasics'), icon: <Badge />, subtitle: uname ? `@${uname}` : undefined, tile: tiles.blue },
      { key: 'category', label: t('visiEditSectionCategory'), icon: <Category />, subtitle: business?.subcategory?.name || business?.category?.name, tile: tiles.teal },
      { key: 'biography', label: t('visiEditSectionBiography'), icon: <Description />, tile: tiles.slate },
      { key: 'website', label: t('visiEditSectionWebsite'), icon: <Language />, subtitle: website || undefined, tile: tiles.sky },
      { key: 'phones', label: t('visiEditSectionPhones'), icon: <Phone />, subtitle: phone1 || undefined, tile: tiles.green },
      { key: 'emails', label: t('visiEditSectionEmails'), icon: <Email />, subtitle: email1 || undefined, tile: tiles.coral },
      { key: 'address', label: t('visiEditSectionAddress'), icon: <LocationOn />, subtitle: address || undefined, tile: tiles.orange },
      { key: 'socials', label: t('visiEditSectionSocials'), icon: <LinkIcon />, tile: tiles.ink },
      { key: 'media', label: t('visiEditSectionMedia'), icon: <Image />, tile: tiles.sky },
      { key: 'payments', label: t('visiEditSectionPayments'), icon: <Payment />, subtitle: t('visiEditPaymentsSubtitle'), tile: tiles.coral },
      { key: 'delete', label: t('visiEditSectionDelete'), icon: <Delete /> },
    ],
    [t, uname, business?.subcategory?.name, business?.category?.name, website, phone1, email1, address]
  );

  const dangerRows = rows.filter((r) => r.key === 'delete');
  const mainRows = rows.filter((r) => r.key !== 'delete');

  return (
    <AppShell title={t('visiEditTitle')} showBackButton maxWidth={false} showCompanion={false}>
      {loadErr && <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert>}
      {saveMsg && <Alert severity="success" sx={{ mb: 2 }}>{saveMsg}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
          gap: 2.25,
          alignItems: 'start',
        }}
      >
        <Box sx={{ position: { md: 'sticky' }, top: { md: 72 } }}>
          <SettingsGroup>
            <Box sx={{ p: 2.25, textAlign: 'center' }}>
              <Avatar
                src={logo || undefined}
                sx={{
                  width: 84,
                  height: 84,
                  mx: 'auto',
                  mb: 1.25,
                  border: `2px solid ${DesignSystem.colors.separator}`,
                  bgcolor: DesignSystem.colors.primaryLight,
                  color: DesignSystem.colors.primary,
                  fontWeight: 700,
                }}
              >
                {(name || '?').charAt(0)}
              </Avatar>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: DesignSystem.colors.textPrimary, lineHeight: 1.2 }}>
                {name || 'VISI'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mt: 0.2, mb: 1.5 }}>
                @{uname || '…'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<QrCode2 />}
                  disabled={!uname}
                  onClick={() => navigate(`/visi/qr/${uname}?name=${encodeURIComponent(name)}`)}
                  sx={{ textTransform: 'none', borderRadius: DesignSystem.borderRadius.pill, boxShadow: 'none', fontWeight: 650 }}
                >
                  {t('qrCode')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  disabled={!uname}
                  onClick={() => navigate(`/${uname}`)}
                  sx={{ textTransform: 'none', borderRadius: DesignSystem.borderRadius.pill, fontWeight: 650 }}
                >
                  {t('visiEditViewPublic')}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate(`/visi/edit/${businessId}/wizard`)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {t('visiEditOpenWizard')}
                </Button>
              </Box>
            </Box>
          </SettingsGroup>
        </Box>

        <Box>
          <SettingsGroupLabel>{t('visiEditProfileDetails')}</SettingsGroupLabel>
          <SettingsGroup>
            {mainRows.map((row) => (
              <Box
                key={row.key}
                component="button"
                type="button"
                onClick={() => setSection(row.key)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.35,
                  width: '100%',
                  border: 0,
                  borderBottom: `1px solid ${DesignSystem.colors.separator}`,
                  background: 'transparent',
                  textAlign: 'left',
                  px: 1.5,
                  py: DesignSystem.layout.rowPadY,
                  cursor: 'pointer',
                  font: 'inherit',
                  '&:last-child': { borderBottom: 0 },
                  '&:hover': { backgroundColor: 'rgba(38, 143, 182, 0.06)' },
                }}
              >
                <IconTile color={row.tile || tiles.blue}>{row.icon}</IconTile>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 650, color: DesignSystem.colors.textPrimary, lineHeight: 1.25 }}>
                    {row.label}
                  </Typography>
                  {row.subtitle && (
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: DesignSystem.colors.textSecondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3,
                      }}
                    >
                      {row.subtitle}
                    </Typography>
                  )}
                </Box>
                <ChevronRight sx={{ color: DesignSystem.colors.textTertiary, fontSize: 18 }} />
              </Box>
            ))}
          </SettingsGroup>

          <SettingsGroupLabel>{t('visiEditDangerZone')}</SettingsGroupLabel>
          <SettingsGroup>
            {dangerRows.map((row) => (
              <Box
                key={row.key}
                component="button"
                type="button"
                onClick={() => setSection(row.key)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.35,
                  width: '100%',
                  border: 0,
                  background: 'transparent',
                  textAlign: 'left',
                  px: 1.5,
                  py: 1.2,
                  cursor: 'pointer',
                  font: 'inherit',
                  '&:hover': { backgroundColor: DesignSystem.colors.errorSoft },
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '8px',
                    bgcolor: DesignSystem.colors.errorSoft,
                    color: DesignSystem.colors.error,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    '& svg': { fontSize: 17 },
                  }}
                >
                  {row.icon}
                </Box>
                <Typography sx={{ flex: 1, fontSize: 14.5, fontWeight: 650, color: DesignSystem.colors.error }}>
                  {row.label}
                </Typography>
                <ChevronRight sx={{ color: DesignSystem.colors.textTertiary, fontSize: 18 }} />
              </Box>
            ))}
          </SettingsGroup>
        </Box>
      </Box>

      <Dialog open={Boolean(section)} onClose={() => setSection(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>
          {rows.find((r) => r.key === section)?.label || t('visiEditEdit')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {saveErr && <Alert severity="error">{saveErr}</Alert>}
          {section === 'basics' && (
            <>
              <TextField label={t('visiWizardDisplayName')} value={name} onChange={(e) => setName(e.target.value)} fullWidth />
              <TextField label={t('visiWizardUsername')} value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} fullWidth helperText={t('visiEditUsernameHint')} />
            </>
          )}
          {section === 'category' && (
            <>
              <FormControl fullWidth>
                <InputLabel>{t('visiWizardCategory')}</InputLabel>
                <Select label={t('visiWizardCategory')} value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setSubcategoryId(''); setSubsubcategoryId(''); }}>
                  {tree.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('visiWizardSubcategory')}</InputLabel>
                <Select label={t('visiWizardSubcategory')} value={subcategoryId} onChange={(e) => { setSubcategoryId(e.target.value); setSubsubcategoryId(''); }}>
                  {subs.map((s) => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
              {subsubs.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>{t('visiEditSubSubcategory')}</InputLabel>
                  <Select label={t('visiEditSubSubcategory')} value={subsubcategoryId} onChange={(e) => setSubsubcategoryId(e.target.value)}>
                    {subsubs.map((s) => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
            </>
          )}
          {section === 'biography' && (
            <TextField label={t('visiEditBiography')} multiline minRows={4} value={biography} onChange={(e) => setBiography(e.target.value)} fullWidth />
          )}
          {section === 'website' && (
            <TextField label={t('website')} value={website} onChange={(e) => setWebsite(e.target.value)} fullWidth placeholder="https://…" />
          )}
          {section === 'phones' && (
            <>
              <TextField label={t('visiEditPhone1')} value={phone1} onChange={(e) => setPhone1(e.target.value)} fullWidth helperText={t('visiEditPhoneHint')} />
              <TextField label={t('visiEditPhone2')} value={phone2} onChange={(e) => setPhone2(e.target.value)} fullWidth />
            </>
          )}
          {section === 'emails' && (
            <>
              <TextField label={t('visiEditEmail1')} value={email1} onChange={(e) => setEmail1(e.target.value)} fullWidth />
              <TextField label={t('visiEditEmail2')} value={email2} onChange={(e) => setEmail2(e.target.value)} fullWidth />
            </>
          )}
          {section === 'address' && (
            <>
              <TextField label={t('visiEditSectionAddress')} value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
              <TextField label={t('visiEditLatitude')} value={lat} onChange={(e) => setLat(e.target.value)} fullWidth />
              <TextField label={t('visiEditLongitude')} value={lng} onChange={(e) => setLng(e.target.value)} fullWidth />
            </>
          )}
          {section === 'socials' && (
            <>
              <FormControl fullWidth>
                <InputLabel>{t('visiEditNetwork')}</InputLabel>
                <Select
                  label={t('visiEditNetwork')}
                  value={socialTypeName}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSocialTypeName(next);
                    setSocialValue(existingSocialValueOf(next));
                  }}
                >
                  {socialTypes.map((s) => <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label={t('visiEditHandleOrUrl')} value={socialValue} onChange={(e) => setSocialValue(e.target.value)} fullWidth />
            </>
          )}
          {section === 'media' && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('visiEditMediaHint')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={
                    logoFile
                      ? URL.createObjectURL(logoFile)
                      : logo || undefined
                  }
                  sx={{ width: 72, height: 72 }}
                />
                <Button variant="outlined" component="label">
                  {t('visiEditChooseLogo')}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  />
                </Button>
              </Box>
              {logoFile && (
                <Typography variant="caption">{logoFile.name}</Typography>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {[0, 1, 2, 3].map((idx) => {
                  const existing = business?.covers?.[idx]?.cover;
                  const preview = coverFiles[idx]
                    ? URL.createObjectURL(coverFiles[idx] as File)
                    : existing
                      ? getBusinessCoverUrl(existing)
                      : null;
                  return (
                    <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box
                        sx={{
                          height: 88,
                          borderRadius: 1,
                          backgroundColor: DesignSystem.colors.surfaceElevated,
                          backgroundImage: preview ? `url(${preview})` : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          border: `1px solid ${DesignSystem.colors.separator}`,
                        }}
                      />
                      <Button size="small" variant="outlined" component="label">
                        {t('visiEditCoverSlot', { n: idx + 1 })}
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setCoverFiles((prev) => {
                              const next = [...prev];
                              next[idx] = file;
                              return next;
                            });
                          }}
                        />
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
          {section === 'payments' && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('visiEditPaymentsHint')}
              </Typography>
              <TextField label={t('visiEditTwintLabel')} value={twint} onChange={(e) => setTwint(e.target.value)} fullWidth />
              <TextField label={t('visiEditPaypalLabel')} value={paypal} onChange={(e) => setPaypal(e.target.value)} fullWidth />
            </>
          )}
          {section === 'delete' && (
            <>
              <Alert severity="warning">{t('visiEditDeleteWarning')}</Alert>
              <FormControl fullWidth>
                <InputLabel>{t('deleteAccountReasonLabel')}</InputLabel>
                <Select label={t('deleteAccountReasonLabel')} value={deleteReasonId} onChange={(e) => setDeleteReasonId(e.target.value)}>
                  {deleteReasons.map((r) => (
                    <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label={t('deleteAccountMessageLabel')} multiline minRows={3} value={deleteMessage} onChange={(e) => setDeleteMessage(e.target.value)} fullWidth />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSection(null)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            color={section === 'delete' ? 'error' : 'primary'}
            disabled={saving || (section === 'socials' && (!socialTypeName || !socialValue.trim()))}
            onClick={() => void saveSection()}
          >
            {saving ? t('saving') : section === 'delete' ? t('delete') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
